import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAssetClasses } from "@/lib/assetClasses";
import { useAuth } from "@/lib/auth";
import { Sparkle, X, Check, CaretDown, CaretUp } from "@phosphor-icons/react";

/**
 * ClassSuggestionBanner (v3.5)
 *
 * Surfaces Leo's proactive class suggestions on the dashboard and on the
 * /assets index. Polls GET /asset-classes/suggestions on mount + on
 * ledgerId change. Accept creates the class and bulk-reassigns the listed
 * assets; Dismiss suppresses the same label for 30 days.
 *
 * Renders nothing when there are no pending suggestions (silent by default).
 */
export default function ClassSuggestionBanner({ onChange }) {
  const { user, activeLedgerId } = useAuth();
  const { refresh: refreshClasses } = useAssetClasses();
  const [pending, setPending] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [previews, setPreviews] = useState({}); // suggestionId -> [{id,name}]

  const role = user?.role || "owner";
  const canWrite = role !== "viewer" && role !== "executor";

  const refetch = async () => {
    try {
      const r = await api.get("/asset-classes/suggestions");
      setPending(r.data || []);
    } catch {
      setPending([]);
    }
  };

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/asset-classes/suggestions");
        if (!cancelled) setPending(r.data || []);
      } catch {
        if (!cancelled) setPending([]);
      }
    })();
    return () => { cancelled = true; };
  }, [user, activeLedgerId]);

  const onAccept = async (sug) => {
    if (!canWrite) return;
    setBusyId(sug.id);
    try {
      const r = await api.post(`/asset-classes/suggestions/${sug.id}/accept`);
      toast.success(`"${sug.suggested_label}" added · ${r.data.moved} asset(s) reassigned.`);
      await refreshClasses();
      await refetch();
      onChange && onChange();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not create class.");
    } finally {
      setBusyId(null);
    }
  };

  const onDismiss = async (sug) => {
    if (!canWrite) return;
    setBusyId(sug.id);
    try {
      await api.post(`/asset-classes/suggestions/${sug.id}/dismiss`);
      toast.success(`Suggestion dismissed.`);
      await refetch();
      onChange && onChange();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not dismiss.");
    } finally {
      setBusyId(null);
    }
  };

  const toggleExpand = async (sug) => {
    if (expandedId === sug.id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(sug.id);
    if (previews[sug.id]) return;
    // Load just the names for the listed asset_ids so the user sees
    // WHICH items Leo grouped before accepting.
    try {
      const r = await api.get(`/assets`);
      const all = r.data || [];
      const idx = new Map(all.map((a) => [a.id, a]));
      const items = (sug.asset_ids || []).map((id) => idx.get(id)).filter(Boolean);
      setPreviews((prev) => ({ ...prev, [sug.id]: items }));
    } catch {
      setPreviews((prev) => ({ ...prev, [sug.id]: [] }));
    }
  };

  if (!pending.length) return null;

  return (
    <div className="space-y-3" data-testid="class-suggestion-banner">
      {pending.map((sug) => {
        const expanded = expandedId === sug.id;
        const previewItems = previews[sug.id] || [];
        const isBusy = busyId === sug.id;
        return (
          <div
            key={sug.id}
            data-testid={`class-suggestion-${sug.id}`}
            className="border border-[#E0DDD6] border-l-4 border-l-[#C5A059] bg-white p-5 shadow-sm"
          >
            <div className="flex items-start gap-4">
              {/* Leo mark */}
              <div className="shrink-0 w-9 h-9 bg-[#1A3622] flex items-center justify-center border border-[#0F2818]">
                <Sparkle size={16} weight="fill" color="#C5A059" />
              </div>
              {/* Body */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#1A3622] font-bold">
                    Leo&apos;s Note
                  </span>
                  <span className="font-mono-ledger text-[9px] uppercase tracking-[0.2em] text-[#5C5F58]">
                    · {sug.confidence} confidence · {sug.asset_ids?.length || 0} item(s)
                  </span>
                </div>
                <h3 className="font-serif-display text-xl text-[#0A192F] mt-1 leading-snug">
                  Create a “{sug.suggested_label}” class?
                </h3>
                <p className="text-sm text-[#5C5F58] mt-1.5 italic font-serif-display">
                  {sug.rationale}
                </p>
                {/* Expandable preview list */}
                {expanded && (
                  <div
                    data-testid={`class-suggestion-${sug.id}-preview`}
                    className="mt-3 border border-[#E0DDD6] bg-[#F9F8F6] divide-y divide-[#E0DDD6]"
                  >
                    {previewItems.length === 0 ? (
                      <div className="px-3 py-2 text-xs text-[#5C5F58] font-mono-ledger uppercase tracking-[0.2em]">
                        Loading…
                      </div>
                    ) : (
                      previewItems.map((a) => (
                        <div key={a.id} className="px-3 py-2 text-sm text-[#0A192F] flex items-center justify-between">
                          <span className="truncate">{a.name}</span>
                          <span className="font-mono-ledger text-[10px] uppercase tracking-[0.2em] text-[#5C5F58] shrink-0 ml-3">
                            {a.brand || ""}
                          </span>
                        </div>
                      ))
                    )}
                  </div>
                )}
                {/* Actions row */}
                <div className="flex flex-wrap items-center gap-2 mt-4">
                  {canWrite ? (
                    <>
                      <button
                        onClick={() => onAccept(sug)}
                        disabled={isBusy}
                        data-testid={`class-suggestion-${sug.id}-accept`}
                        className="inline-flex items-center gap-2 bg-[#1A3622] text-[#F9F8F6] px-3.5 py-1.5 text-xs border border-[#0F2818] hover:bg-[#0F2818] disabled:opacity-50"
                      >
                        <Check size={12} weight="bold" />
                        {isBusy ? "Working…" : "Accept & reassign"}
                      </button>
                      <button
                        onClick={() => onDismiss(sug)}
                        disabled={isBusy}
                        data-testid={`class-suggestion-${sug.id}-dismiss`}
                        className="inline-flex items-center gap-2 border border-[#E0DDD6] text-[#5C5F58] px-3.5 py-1.5 text-xs hover:border-[#5C5F58]"
                      >
                        <X size={12} weight="bold" /> Dismiss
                      </button>
                    </>
                  ) : (
                    <span className="font-mono-ledger text-[10px] uppercase tracking-[0.2em] text-[#5C5F58]">
                      Read-only access · ask an editor to apply
                    </span>
                  )}
                  <button
                    onClick={() => toggleExpand(sug)}
                    data-testid={`class-suggestion-${sug.id}-toggle`}
                    className="inline-flex items-center gap-1 text-xs text-[#5C5F58] hover:text-[#1A3622] underline-offset-2 hover:underline ml-1"
                  >
                    {expanded ? <CaretUp size={12} /> : <CaretDown size={12} />}
                    {expanded ? "Hide items" : "Show me which"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
